import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { DeleteOutlined, EditOutlined, LockOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Table, Tooltip } from 'antd';
import MarketplaceFeeModal from "./MarketplaceFeeModal";
import MarketplaceFeeType from "../../enums/MarketplaceFeeType";
import UnitOfMeasureType from "../../enums/UnitOfMeasureType";
import PermissionDropdownButton from "../../components/PermissionDropdownButton";
import { conditionFilter } from "../../../../swzutils";
import { FeeType } from "../orders/api/Representation";
import { CurrencyFormat } from '../../components/FormattersTS';
const MarketplaceFeesTable = ({ fees, onFeeAdded, onFeeUpdated, onFeeRemoved, canEdit, canRemove, showTotals }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [editFee, setEditFee] = useState(null);
    const onAddClicked = () => {
        setModalVisible(true);
        setEditFee(null);
    };
    const onEditClicked = (fee) => {
        setModalVisible(true);
        setEditFee(fee);
    };
    const onRemoveClicked = (fee) => {
        onFeeRemoved(fee);
    };
    const onModalSaveClicked = (fee) => {
        if (editFee) {
            updateFee(fee);
        }
        else {
            addFee(fee);
        }
        hideModal();
    };
    const addFee = (fee) => {
        onFeeAdded(fee);
    };
    const updateFee = (fee) => {
        onFeeUpdated(fee);
    };
    const onCancelModalClicked = () => {
        hideModal();
    };
    const hideModal = () => {
        setModalVisible(false);
        setEditFee(null);
    };
    const buildColumns = () => {
        const columns = [{
                title: 'Description',
                dataIndex: 'description',
                width: '15%'
            }, {
                title: 'Type',
                dataIndex: 'type',
                width: '8%',
                render: (text /*, record*/) => {
                    const enumObj = MarketplaceFeeType.valueOf(text);
                    if (enumObj) {
                        return enumObj.description;
                    }
                    return "Unknown:" + text;
                }
            }, {
                title: 'Unit of Measure',
                dataIndex: 'unitOfMeasure',
                width: '10%',
                render: (text, record) => {
                    return record.type === FeeType.PER_UOM
                        ? UnitOfMeasureType.enumDescription(record.unitOfMeasure)
                        : "";
                }
            }, {
                title: 'Quantity',
                dataIndex: 'quantity',
                width: '5%',
                align: 'right',
                condition: showTotals,
            }, {
                title: 'Provider',
                dataIndex: ['providerTenant', 'name'],
                width: '15%',
                render: (text, record) => record.providerTenant
                    ? text
                    : _jsx("i", { children: "The contractor, once a vehicle is allocated" })
            }, {
                title: 'Provider Amount',
                dataIndex: 'providerAmountExTax',
                width: '8%',
                align: 'right',
                render: (text /*, record*/) => (_jsx(CurrencyFormat, { value: text }))
            }, {
                title: 'Provider Total',
                dataIndex: 'providerTotalExTax',
                width: '8%',
                align: 'right',
                condition: showTotals,
                render: (text /*, record*/) => (_jsx(CurrencyFormat, { value: text }))
            }, {
                title: 'Accounts',
                dataIndex: 'providerAccountsType',
                width: '8%',
                align: 'right',
                render: (text /*, record*/) => {
                    return text;
                }
            }, {
                title: 'Charge',
                dataIndex: 'chargeAmountExTax',
                width: '5%',
                align: 'right',
                render: (text /*, record*/) => (_jsx(CurrencyFormat, { value: text }))
            }, {
                title: 'Final Charge',
                dataIndex: 'chargeTotalExTax',
                width: '8%',
                align: 'right',
                condition: showTotals,
                render: (text /*, record*/) => (_jsx(CurrencyFormat, { value: text }))
            }, {
                title: 'Action',
                key: 'action',
                width: '10%',
                condition: canEdit || canRemove,
                render: (text, record) => record.fixedFee
                    ? (_jsx(Tooltip, { title: "This fee is applied automatically for the vehicle type and cannot be changed or removed", children: _jsx("span", { children: _jsx(Button, { disabled: true, icon: _jsx(LockOutlined, {}), children: "Fixed" }) }) }))
                    : (_jsx(PermissionDropdownButton, { label: (_jsxs(_Fragment, { children: [_jsx(EditOutlined, {}), " Edit"] })), onClick: () => onEditClicked(record), menuItems: [{
                                key: "app/fee/delete",
                                onClick: () => onRemoveClicked(record),
                                condition: canRemove,
                                icon: _jsx(DeleteOutlined, {}),
                                label: "Remove"
                            }] }))
            }];
        return columns.filter(conditionFilter);
    };
    const memoizedColumns = useMemo(() => {
        return buildColumns();
    }, [canEdit, showTotals]);
    return (_jsxs("div", { children: [canEdit &&
                _jsx("div", { className: "table-operations-inline", children: _jsx(Button, { icon: _jsx(PlusOutlined, {}), onClick: onAddClicked, children: "Add a Fee" }) }), _jsx(Table, { columns: memoizedColumns, size: "middle", rowKey: record => record.id, dataSource: fees, pagination: false }), _jsx("p", { children: _jsx("i", { children: "NOTE: All amounts exclude GST" }) }), _jsx(MarketplaceFeeModal, { open: modalVisible, fee: editFee, onCancel: onCancelModalClicked, onSave: onModalSaveClicked })] }));
};
export default MarketplaceFeesTable;
